import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { leaveApi } from './leaveApi';

export default function TenantLeaveConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await leaveApi.getConfig();
      setConfig(data);
    } catch (err) {
      toast.error('Failed to load leave configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await leaveApi.updateConfig(config);
      setConfig(updated);
      toast.success('Tenant leave configuration saved successfully.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update leave configuration.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* DEC-001 Statutory Warning Banner */}
      <Alert
        severity="warning"
        icon={<GavelIcon fontSize="inherit" />}
        sx={{
          borderRadius: '16px',
          border: '1px solid #fef08a',
          bgcolor: '#fefce8',
          color: '#713f12',
          '& .MuiAlert-icon': { color: '#ca8a04' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>DEC-001 Statutory Compliance & CA Signoff Notice</AlertTitle>
        Under Indian labour statutes (Factories Act 1948, State Shops & Commercial Establishments Acts), Loss of Pay (LOP)
        calculation bases and salary deduction denominators must strictly align with your establishment standing orders.
        These settings are configurable for governance modeling, but <strong>direct payroll deductions remain inactive</strong> pending
        formal signoff by your certified Chartered Accountant or legal counsel.
      </Alert>

      {/* LOP Calculation Engine */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
          Loss of Pay (LOP) Calculation Engine
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Define the statutory divisor and compensation base used when unpaid leaves or salary deductions are evaluated.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Per-Day Denominator Basis</InputLabel>
              <Select
                value={config?.lop_calculation_basis || 'CALENDAR_DAYS'}
                label="Per-Day Denominator Basis"
                onChange={(e) => handleChange('lop_calculation_basis', e.target.value)}
              >
                <MenuItem value="CALENDAR_DAYS">Actual Calendar Days in Month (28/29/30/31)</MenuItem>
                <MenuItem value="FIXED_30">Fixed 30 Days Standard</MenuItem>
                <MenuItem value="WORKING_DAYS">Scheduled Working Days (Excluding Weekoffs & Holidays)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Salary Deduction Base</InputLabel>
              <Select
                value={config?.lop_salary_deduction_base || 'BASIC_PLUS_DA'}
                label="Salary Deduction Base"
                onChange={(e) => handleChange('lop_salary_deduction_base', e.target.value)}
              >
                <MenuItem value="BASIC_PLUS_DA">Basic + Dearness Allowance (Statutory Baseline)</MenuItem>
                <MenuItem value="GROSS_SALARY">Gross Salary (Total Monthly Cash Earnings)</MenuItem>
                <MenuItem value="FIXED_ALLOWANCES">Fixed Allowances (Basic + DA + HRA + Special)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Sandwich Policy Governance */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
          Sandwich Policy Governance
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Controls whether weekends, weekly off-days, or declared gazetted holidays between consecutive leaves count as leave days.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config?.sandwich_rule_enabled)}
                  onChange={(e) => handleChange('sandwich_rule_enabled', e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Sandwich Rule"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config?.sandwich_on_holidays)}
                  onChange={(e) => handleChange('sandwich_on_holidays', e.target.checked)}
                  disabled={!config?.sandwich_rule_enabled}
                  color="primary"
                />
              }
              label="Include Public Holidays"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config?.sandwich_includes_unexcused_absences)}
                  onChange={(e) => handleChange('sandwich_includes_unexcused_absences', e.target.checked)}
                  disabled={!config?.sandwich_rule_enabled}
                  color="primary"
                />
              }
              label="Include Unexcused Absences"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stale Leave Approval Policies */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
          Stale Application Management
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Automated governance for leave requests left unanswered by managers past the configured SLA threshold.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Stale Threshold (Days)"
              value={config?.stale_approval_threshold_days || 7}
              onChange={(e) => handleChange('stale_approval_threshold_days', parseInt(e.target.value, 10))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Counting Mode</InputLabel>
              <Select
                value={config?.stale_approval_counting_mode || 'BUSINESS_DAYS'}
                label="Counting Mode"
                onChange={(e) => handleChange('stale_approval_counting_mode', e.target.value)}
              >
                <MenuItem value="BUSINESS_DAYS">Business Days (Excl. Weekoffs/Holidays)</MenuItem>
                <MenuItem value="CALENDAR_DAYS">Actual Calendar Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Action if Insufficient Balance</InputLabel>
              <Select
                value={config?.stale_approval_unfunded_action || 'ESCALATE_TO_HR'}
                label="Action if Insufficient Balance"
                onChange={(e) => handleChange('stale_approval_unfunded_action', e.target.value)}
              >
                <MenuItem value="ESCALATE_TO_HR">Escalate to HR Administrator</MenuItem>
                <MenuItem value="POLICY_CONVERT_TO_LOP">Convert to Loss of Pay (LOP)</MenuItem>
                <MenuItem value="AUTO_DECLINE">Auto-Decline Request</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* MOD-05 Batch 3: Automated Accruals & Absence Review Controls */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
          Batch 3: Automated Accruals & Absence Review Controls
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Configure monthly leave accrual automation, unexcused absence scanning, and annual restricted holiday quotas.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config?.is_monthly_accrual_active)}
                  onChange={(e) => handleChange('is_monthly_accrual_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Activate Monthly Accrual Automation"
            />
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
              Executes automatic monthly leave accruals via Celery Beat with pro-ration and balance cap checks.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config?.is_unexcused_absence_processing_active)}
                  onChange={(e) => handleChange('is_unexcused_absence_processing_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Activate Unexcused Absence Candidate Review"
            />
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
              Generates absence review candidates for human HR review without automated payroll or attendance deduction.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Restricted Holiday Annual Quota"
              value={config?.restricted_holiday_annual_quota ?? 2}
              onChange={(e) => handleChange('restricted_holiday_annual_quota', parseInt(e.target.value, 10) || 0)}
              helperText="Maximum optional festival holidays an employee may elect per calendar year."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Statutory Acknowledgement & Notes */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
          Compliance Review & Acknowledgement
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          label="Legal Counsel / CA Compliance Notes"
          value={config?.statutory_notes || ''}
          onChange={(e) => handleChange('statutory_notes', e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={Boolean(config?.statutory_disclaimer_acknowledged)}
              onChange={(e) => handleChange('statutory_disclaimer_acknowledged', e.target.checked)}
              color="primary"
            />
          }
          label="I acknowledge that company leave policies must be independently audited for State Shops & Establishments compliance."
        />
      </Paper>

      {/* Save Action */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 4,
            py: 1.2,
            borderRadius: '12px',
            bgcolor: '#2563eb',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { bgcolor: '#1d4ed8' },
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}
